import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'konsulent-theme';
const isValidTheme = (value: string | null): value is Theme =>
  value === 'light' || value === 'dark' || value === 'system';

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }

  const darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const lightMediaQuery = window.matchMedia('(prefers-color-scheme: light)');

  if (darkMediaQuery.matches) {
    return 'dark';
  }
  if (lightMediaQuery.matches) {
    return 'light';
  }

  return 'dark';
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    const storedTheme = localStorage.getItem(STORAGE_KEY);
    return isValidTheme(storedTheme) ? storedTheme : 'system';
  });
  
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('dark');

  const applyResolvedTheme = useCallback((resolved: 'light' | 'dark') => {
    const root = document.documentElement;
    setResolvedTheme(resolved);

    if (resolved === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    const resolved = theme === 'system' ? getSystemTheme() : theme;
    applyResolvedTheme(resolved);
  }, [theme, applyResolvedTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    if (typeof window.matchMedia !== 'function') return;

    const darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      applyResolvedTheme(getSystemTheme());
    };

    if (typeof darkMediaQuery.addEventListener === 'function') {
      darkMediaQuery.addEventListener('change', handler);
      return () => darkMediaQuery.removeEventListener('change', handler);
    }

    darkMediaQuery.addListener(handler);
    return () => darkMediaQuery.removeListener(handler);
  }, [theme, applyResolvedTheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  };

  const toggleTheme = () => {
    const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeProvider;
